import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { ReusableFunctions } from '../../shared/classes/reusable-functions';
import { DisplayOptions } from '../../shared/interfaces/display-options';
import { NoDataActionType } from '../../shared/interfaces/no-data';
import { CurrentUserStateService } from '../../shared/services/current-user-state.service';
import { ImpersonationService } from '../../shared/services/impersonation.service';
import { ToastService } from '../../shared/services/toast.service';
import { iDashboardCards, iFilterParams, iTracks } from '../interfaces/my-limegear.interface';
import * as Highcharts from 'highcharts';

@Injectable({
    providedIn: 'root',
})
export class CustomDashboardsService {
    private serverUrl = 'https://' + window.location.host; // TODO Get url and version number from appsettings.
    private version = '0.5';
    private componentName = 'customDashboards';
    private _url = (this.serverUrl + '/api/v{version}/{componentName}')
        .replace('{version}', this.version)
        .replace('{componentName}', this.componentName);

    private reusableFunctions = new ReusableFunctions(this.router);
    private filterChangesSubject = new BehaviorSubject<iFilterParams>({} as iFilterParams);
    filterChangesSubject$ = this.filterChangesSubject.asObservable();
    private dashboardCardList = new BehaviorSubject<iDashboardCards[]>([]);
    dashboardCardList$ = this.dashboardCardList.asObservable();

    constructor(
        private router: Router,
        private _http: HttpClient,
        private _toastService: ToastService,
        private impersonationService: ImpersonationService,
        private currentUserStateService: CurrentUserStateService
    ) {}

    getDasboardCardsByClient(dashboardId): Observable<Array<iDashboardCards>> {
        let parameters = {
            dashboardId: dashboardId,
        };
        return this._http.post<Array<iDashboardCards>>(
            this._url + '/GetDasboardCardsByClient/',
            parameters,
            {
                headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            }
        );
    }

    deleteClientDasboardCard(dashboardId: string, cardId: string): Observable<number> {
        let parameters = new HttpParams().set('dashboardId', dashboardId).set('CardId', cardId);
        return this._http.get<number>(this._url + '/DeleteClientDasboardCard', {
            params: parameters,
        });
    }

    addClientDasboardCard(dashboardId: string, cardId: string): Observable<number> {
        let parameters = new HttpParams().set('dashboardId', dashboardId).set('CardId', cardId);
        return this._http.get<number>(this._url + '/AddClientDasboardCard', { params: parameters });
    }

    updateClientDasboardCards(dashboardId: string, cardsInfoList: object[]): Observable<number> {
        let parameters = {
            DashboardId: dashboardId,
            CardsInfoJsonString: JSON.stringify(cardsInfoList),
        };
        return this._http.post<number>(this._url + '/UpdateClientDasboardCards/', parameters, {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        });
    }

    addWidget(dashboard: string, cardId: string) {
        this.addClientDasboardCard(dashboard, cardId).subscribe((success) => {
            if (success > 0) {
                this._toastService.success('Successfully added to the dashboard!');
            } else {
                this._toastService.warning('The card is already in the dashboard.');
            }
        });
    }

    removeWidget(dashboard: string, cardId: string) {
        this.deleteClientDasboardCard(dashboard, cardId).subscribe((success) => {
            if (success > 0) {
                let state = this.dashboardCardList.getValue();
                state = state.filter((item) => item.cardId !== cardId);
                this.dashboardCardList.next(state); // TODO: Only refresh same card group
                this._toastService.success('The card is removed successfully!');
            } else {
                this._toastService.warning('Plese try again!');
            }
        });
    }

    updateCorporateDashboardCardList(dashboardCards: iDashboardCards[]) {
        this.dashboardCardList.unsubscribe();
        this.dashboardCardList = new BehaviorSubject<iDashboardCards[]>([]);
        this.dashboardCardList$ = this.dashboardCardList.asObservable();
        this.dashboardCardList.next(dashboardCards);
    }

    updateDashboardCardsTrack() {
        let dashboardCards: iDashboardCards[] = this.dashboardCardList.getValue();
        let tracks: iTracks[] = [];
        let uniqueGroups = this.reusableFunctions.getUniqueItemList(dashboardCards, 'cardGroup');
        uniqueGroups.forEach((cardGroup) => {
            let group = dashboardCards.filter((item) => item.cardGroup === cardGroup);
            group.sort((a, b) => a.cardOrder - b.cardOrder);
            let track: iTracks = {
                cardsIdArray: this.reusableFunctions.getUniqueItemList(group, 'cardId'),
                group: cardGroup,
                groupOrder: group[0].cardGroupOrder,
                class: group[0].cardGroupClass,
            };
            tracks.push(track);
        });

        return tracks.sort((a, b) => a.groupOrder - b.groupOrder);
    }

    getDisplayOptions(displayOptions: DisplayOptions[]) {
        if (!displayOptions || displayOptions.length === 0) {
            let routerUrl = this.router.url.split('?')[0];
            displayOptions = this.reusableFunctions.getCurrentDisplayOptions(routerUrl);
        }
        return displayOptions;
    }

    getHierarchyFilters(hFilterJson: string) {
        if (hFilterJson === '' || hFilterJson === '[]' || !hFilterJson) {
            hFilterJson = this.reusableFunctions.getHierarchyFilters();
        }
        return hFilterJson;
    }

    detectChange(
        hFilterJson: string,
        displayOptions: DisplayOptions[],
        isHierarchyFilter: boolean,
        isDisplayOption: boolean,
        action: NoDataActionType,
        dashboard: string
    ) {
        let url = this.router.url.split('?');
        if (url[0].includes(dashboard)) {
            let change: iFilterParams = {
                isHierarchyFilter: isHierarchyFilter,
                isDisplayOption: isDisplayOption,
                displayOptions: this.getDisplayOptions(displayOptions),
                hierarchyfilters: this.getHierarchyFilters(hFilterJson),
                action: action,
            };

            if (change.hierarchyfilters && change.displayOptions && change.action) {
                if (dashboard !== 'corporate') {
                    // TODO: Check why the corporate page is not refreshing with this unsubscribe
                    this.unsubscribeFilters();
                }
                this.filterChangesSubject.next(change);
            }
        }
    }

    hasEditPermission() {
        let hasPermission = false;
        if (this.impersonationService.impersonateUser) {
            hasPermission = this.impersonationService.isImpersonatingPowerUser();
        } else {
            hasPermission =
                this.currentUserStateService.isLBASuperUser() ||
                this.currentUserStateService.isPowerUser();
        }
        return hasPermission;
    }

    unsubscribeFilters() {
        this.filterChangesSubject.unsubscribe();
        this.filterChangesSubject = new BehaviorSubject<iFilterParams>({} as iFilterParams);
        this.filterChangesSubject$ = this.filterChangesSubject.asObservable();
    }

    reCreateExistingCharts() {
        setTimeout(() => {
            Highcharts.charts.forEach((chart) => {
                try {
                    if (chart?.options && chart['renderTo'] && chart['renderTo'].id) {
                        let options = Object.assign({}, chart.options);
                        let id = chart['renderTo'].id;
                        chart.destroy();
                        chart = new Highcharts.Chart(id, options);
                    }
                } catch (e) {}
            });
        }, 400);
    }

    destroyCharts() {
        Highcharts.charts.forEach((chart) => {
            try {
                chart.destroy();
            } catch (e) {}
        });
    }
}
